import { create } from 'zustand';

export type WallpaperMode = 'cheap' | 'flat' | 'grid' | 'mesh';
export type DensityMode = 'comfortable' | 'compact';
export type AccentMode = 'copper' | 'blue' | 'forest' | 'mono';

export interface Appearance {
	wallpaper: WallpaperMode;
	grain: boolean;
	density: DensityMode;
	accent: AccentMode;
	lightCallouts: boolean;
}

export const DEFAULT_APPEARANCE: Appearance = {
	wallpaper: 'cheap',
	grain: false,
	density: 'comfortable',
	accent: 'copper',
	lightCallouts: true,
};

export const APPEARANCE_STORAGE_KEY = 'khal:appearance';

interface AppearanceStore extends Appearance {
	set: (patch: Partial<Appearance>) => void;
	replace: (next: Appearance) => void;
	reset: () => void;
}

function loadInitial(): Appearance {
	if (typeof window === 'undefined') return DEFAULT_APPEARANCE;
	try {
		const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
		if (!raw) return DEFAULT_APPEARANCE;
		const parsed = JSON.parse(raw) as Partial<Appearance>;
		return { ...DEFAULT_APPEARANCE, ...parsed };
	} catch {
		return DEFAULT_APPEARANCE;
	}
}

function persist(state: Appearance): void {
	if (typeof window === 'undefined') return;
	try {
		const snapshot: Appearance = {
			wallpaper: state.wallpaper,
			grain: state.grain,
			density: state.density,
			accent: state.accent,
			lightCallouts: state.lightCallouts,
		};
		window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(snapshot));
	} catch {
		// Swallow quota / SecurityError — appearance is non-critical.
	}
}

export const useAppearanceStore = create<AppearanceStore>((setState, getState) => {
	const initial = loadInitial();
	return {
		...initial,
		set: (patch) => {
			setState(patch);
			const next = getState();
			persist({
				wallpaper: next.wallpaper,
				grain: next.grain,
				density: next.density,
				accent: next.accent,
				lightCallouts: next.lightCallouts,
			});
		},
		replace: (next) => {
			setState(next);
			persist(next);
		},
		reset: () => {
			setState(DEFAULT_APPEARANCE);
			persist(DEFAULT_APPEARANCE);
		},
	};
});

function applyFromStorage(raw: string | null): void {
	if (!raw) return;
	try {
		const parsed = JSON.parse(raw) as Partial<Appearance>;
		useAppearanceStore.setState({ ...DEFAULT_APPEARANCE, ...parsed });
	} catch {
		// Ignore malformed payloads.
	}
}

declare global {
	interface Window {
		__khalAppearanceSetItemHooked?: boolean;
	}
}

if (typeof window !== 'undefined') {
	// Cross-tab updates: the browser fires `storage` only in OTHER documents
	// that share the origin, so this branch handles the multi-tab case.
	window.addEventListener('storage', (event) => {
		if (event.key !== APPEARANCE_STORAGE_KEY || !event.newValue) return;
		applyFromStorage(event.newValue);
	});

	// Same-window updates: `storage` does NOT fire in the document that wrote.
	// pack-settings' AppearanceTab is loaded via dynamic import — same window,
	// same origin — and writes its own `localStorage.setItem(...)`. Without
	// this hook, the desktop's appearance store never sees those writes and
	// `<body data-accent>` / `data-density` stay stale until a reload.
	if (!window.__khalAppearanceSetItemHooked) {
		window.__khalAppearanceSetItemHooked = true;
		const protoSetItem = Storage.prototype.setItem;
		Storage.prototype.setItem = function patchedSetItem(this: Storage, key: string, value: string) {
			const prev = this === window.localStorage && key === APPEARANCE_STORAGE_KEY ? this.getItem(key) : null;
			protoSetItem.call(this, key, value);
			if (this === window.localStorage && key === APPEARANCE_STORAGE_KEY && prev !== value) {
				applyFromStorage(value);
			}
		};
	}
}
